import { useCallback, useEffect, useState } from 'react';
import {
  RoleDetail,
  RolePermissionCatalogue,
  fetchPermissionCatalogue,
  fetchRoleDetail,
} from './roleDetailService';
import { readErrorMessage } from '../../utils/errorMessage';

export interface UseRoleDetail {
  role: RoleDetail | null;
  permissions: RolePermissionCatalogue[];
  loading: boolean;
  error: string | null;
  /** Re-run the load — what the inline error's Retry is wired to. */
  reload: () => void;
}

/**
 * Load a role, and always ARRIVE somewhere.
 *
 * The three states are exhaustive: loading, an error with a retry, or a role.
 * There is deliberately no `hasInitialized` ref — that made the fetch
 * unrepeatable, so opening a second role in the same mounted component (which
 * is what happens now the detail is a sub-view of the section) left the page
 * on "Loading…" forever. The effect is keyed on the id instead, `alive` drops
 * a response that arrives after you have moved on, and `setLoading(false)`
 * runs in a `finally` so no path can skip it.
 */
export const useRoleDetail = (roleId: string): UseRoleDetail => {
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [permissions, setPermissions] = useState<RolePermissionCatalogue[]>([]);
  const [loading, setLoading] = useState(!!roleId);
  const [error, setError] = useState<string | null>(roleId ? null : 'No role id in this link.');
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!roleId) {
      setRole(null);
      setLoading(false);
      setError('No role id in this link.');
      return undefined;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [loadedRole, catalogue] = await Promise.all([
          fetchRoleDetail(roleId),
          // The catalogue is chrome for the permission list; losing it should
          // not lose the role.
          fetchPermissionCatalogue().catch(() => []),
        ]);
        if (!alive) return;
        setRole(loadedRole);
        setPermissions(catalogue);
      } catch (err: any) {
        if (!alive) return;
        setRole(null);
        setError(
          readErrorMessage(err, 'Could not load this role.')
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [roleId, attempt]);

  return { role, permissions, loading, error, reload };
};

export default useRoleDetail;
